<script setup>

import {onMounted, reactive, watch,ref,shallowRef } from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'ManageRoleComponent';

const props = defineProps({
  status: String,
  create: Boolean,
  update: Boolean,
  view: Boolean,
  roleId: Number,
  updateNum: Number
});

const emit = defineEmits(['closed','update']);

const permissionTree = ref();
const permissions = shallowRef([])

const data = reactive({
  loading: true,
  is_default: false,
  dialog: true,
  permissions: {},
  opened: [],
  editedItem: {
    name: ""
  }
});

const close = () => {
  emit('closed');
}

const save = async () => {
  if(!data.loading && (props.create || props.update)) {
    data.loading = true;

    // Create new auth role
    if(props.create) {
      await ApiWrapper.post('auth-roles', 'create-role', {
            newRole: {
              role: data.editedItem.name,
              permissions: permissions.value
            }
          })
          .then((response) => {
            emit('update');
            close();

            activeComponentStore.notifyComponents(['auth-roles'],compName);

            ToastUtil.successMsg('Created role');
          })
          .catch((e) => {
            ToastUtil.error(e);
          })
          .finally(() => {
            data.loading = false;
          });
    }
    // Update existing auth role
    else if(props.update){
      await ApiWrapper.post('auth-roles', 'edit-role', {
            editedItem: {
              id: props.roleId,
              role: data.editedItem.name,
              permissions: permissions.value
            }
          })
          .then((response) => {
            emit('update');
            close();

            activeComponentStore.notifyComponents(['auth-roles'],compName);

            ToastUtil.successMsg('Updated role');
          })
          .catch((e) => {
            ToastUtil.error(e);
          })
          .finally(() => {
            data.loading = false;
          })
    }
  }
}

watch(() => props.updateNum, async (val) => {
  clear();
  if(props.roleId) {
    await ApiWrapper.post('auth-roles', 'get-role', {
      id: props.roleId
    })
    .then((response) => {
      data.is_default = response.role.is_default;
      data.editedItem = {
        name: response.role.role
      };
      permissions.value = response.role.permissions;
      updateOpened(response.role.permissions);
    })
    .catch((e) => {
      ToastUtil.error(e);
    })
    .finally(() => {
      data.loading = false;
    });
  }
});

// Updates which nodes should be opened to show full permissions
const updateOpened = (permissions) => {
  let openedNodes = [];

  for(let i = 0; i < permissions.length; i++) {
    const permission = permissions[i];
    // This is a child node. Make sure all parent nodes are in the openedNodes list
    if(permission.includes('.') && !permission.includes('*')) {
      const words = permission.split('.');
      for(let j = 0; j < words.length-1; j++) {
        openedNodes.push(`${words.slice(0,j+1).join('.')}.*`);
      }
    }
  }

  data.opened = openedNodes;
}

const buildPermissionTree = (prefix, node) => {
  let tree = [];

  for(const key in node) {
    const id = `${prefix !== "" ? `${prefix}.` : ''}${key}`;
    const description = typeof node[key] === 'string' ? node[key]: "";
    const title = key.split('-').map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(' ');

    let val = {
      id: !id.includes('.') ? `${id}.*` : id,
      title: title
    }

    if(typeof node[key] === 'object') {
      const children = buildPermissionTree(id,node[key]);
      if(children.length > 0) {
        val.children = children;
      }
    }

    tree.push(val);
  }

  return tree;
}

const loadPermissions = async () => {
  data.loading = true;
  await ApiWrapper.get('auth-roles', 'list-permissions')
  .then((response) => {
    data.permissions = response.permissions;
    permissionTree.value = buildPermissionTree("",response.permissions);
  })
  .catch((e) => {
    ToastUtil.error(e);
  })
  .finally(()=>{
    data.loading = false;
  });
}

const clear = () => {
  data.editedItem = {
    name: ""
  };
  data.opened = [];
  permissions.value = [];
}

onMounted(async ()=>{
  clear();
  await loadPermissions();
});

</script>

<template>
  <div class="pa-4 text-center">
    <v-dialog
        v-if="props.status === 'open'"
        v-model="data.dialog"
        max-width="600"
        persistent
        @click:outside="close"
    >
      <v-card>
        <v-toolbar flat color="white">
          <v-toolbar-title>{{ props.create ? "Create": ""}} {{ props.update ? "Update" : "" }} {{ props.view ? "View" : "" }} Role</v-toolbar-title>
          <v-progress-linear
              :active="data.loading"
              :indeterminate="data.loading"
              absolute
              bottom
              color="primary"
          >
          </v-progress-linear>
        </v-toolbar>

        <v-card-text>
          <v-row>
            <v-col cols="12" sm="12" class="pb-0">
              <v-text-field
                  v-model="data.editedItem.name"
                  label="Name"
                  :disabled="props.view"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" class="pt-0">
              <v-treeview
                  v-model:selected="permissions"
                  :items="permissionTree"
                  select-strategy="independent"
                  item-value="id"
                  selectable
                  :disabled="props.view"
                  density="compact"
                  :opened="data.opened"
              ></v-treeview>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              text="Close"
              variant="plain"
              @click="close"
          ></v-btn>

          <v-btn
              color="primary"
              text="Update"
              variant="tonal"
              :disabled="data.loading"
              @click="save"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

</style>